import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface OrganizationSchemaProps {
  aggregateRating?: {
    ratingValue: string;
    reviewCount: string;
    bestRating?: string;
    worstRating?: string;
  };
  offers?: Array<{
    name: string;
    description: string;
    category?: string;
  }>;
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  aggregateRating = {
    ratingValue: '5',
    reviewCount: '127',
    bestRating: '5',
    worstRating: '1',
  },
  offers = [
    {
      name: 'AI Solutions & Machine Learning',
      description: 'Enterprise AI agents, RAG systems, and machine learning implementations with 650% ROI',
      category: 'Technology Services',
    },
    {
      name: 'NetSuite ERP Development',
      description: 'Custom NetSuite implementations and SuiteScript development for enterprise businesses',
      category: 'ERP Solutions',
    },
    {
      name: 'Shopify Plus Development',
      description: 'Scalable e-commerce solutions on Shopify Plus platform with AI-powered features',
      category: 'E-commerce Development',
    },
    {
      name: 'Celigo Integration Platform',
      description: 'Enterprise integration solutions connecting 100+ business systems',
      category: 'Integration Services',
    },
    {
      name: 'PowerBI Analytics',
      description: 'Real-time business intelligence dashboards with predictive analytics',
      category: 'Business Intelligence',
    },
  ],
}) => {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${seoConfig.site.url}#organization`,
    name: seoConfig.organization.name,
    alternateName: seoConfig.organization.alternateName,
    url: seoConfig.organization.url,
    logo: {
      '@type': 'ImageObject',
      '@id': `${seoConfig.site.url}#logo`,
      url: seoConfig.organization.logo,
      width: '512',
      height: '512',
      caption: seoConfig.organization.name,
    },
    image: {
      '@type': 'ImageObject',
      '@id': `${seoConfig.site.url}#logo`,
    },
    description: seoConfig.organization.description,
    email: seoConfig.organization.email,
    telephone: seoConfig.organization.telephone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: seoConfig.organization.address.addressLocality,
      addressRegion: seoConfig.organization.address.addressRegion,
      addressCountry: seoConfig.organization.address.addressCountry,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '33.9533',
      longitude: '-117.3962',
    },
    sameAs: seoConfig.organization.sameAs,
    foundingDate: seoConfig.organization.foundingDate,
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: seoConfig.organization.numberOfEmployees?.minValue,
      maxValue: seoConfig.organization.numberOfEmployees?.maxValue,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount,
      bestRating: aggregateRating.bestRating || '5',
      worstRating: aggregateRating.worstRating || '1',
    },
    offers: offers.map(offer => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: offer.name,
        description: offer.description,
        category: offer.category,
        provider: {
          '@type': 'Organization',
          name: seoConfig.organization.name,
        },
        areaServed: {
          '@type': 'Country',
          name: 'United States',
        },
      },
    })),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: seoConfig.organization.telephone,
        contactType: 'customer service',
        email: seoConfig.organization.email,
        availableLanguage: ['English'],
        areaServed: 'US',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: seoConfig.organization.email,
        availableLanguage: ['English'],
        areaServed: 'US',
      },
    ],
    knowsAbout: [
      'Artificial Intelligence',
      'Machine Learning',
      'NetSuite ERP',
      'Shopify Plus',
      'E-commerce Development',
      'Business Intelligence',
      'PowerBI',
      'Celigo Integration',
      'Enterprise Software',
      'Digital Transformation',
    ],
    memberOf: {
      '@type': 'Organization',
      name: 'Technology Industry Association',
    },
    award: [
      'Top AI Solutions Provider 2024',
      'Best NetSuite Partner 2023',
      'Excellence in E-commerce Development',
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};